import React, { useEffect, useRef, useState } from 'react'
import { clearAllCaches, isInstalledPWA, networkStatus } from './pwaUtils'

/**
 * React hook to check if the application is online/offline
 *
 * ROOT CAUSE EXPLANATION:
 * The browser's navigator.onLine property and online/offline events can be unreliable:
 * 1. They only detect network adapter connectivity, not actual internet access
 * 2. Brief network interruptions trigger events even when connection is stable
 * 3. Some browsers fire false offline events during high CPU usage or network congestion
 *
 * SOLUTION: Debouncing prevents false positives by waiting before confirming state changes
 *
 * @returns {boolean} True if online, false if offline
 */
export function useIsOnline(): boolean {
  const [isOnline, setIsOnline] = useState(networkStatus.isOnline)
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null)
  const lastStateRef = useRef(networkStatus.isOnline)

  useEffect(() => {
    const handleOnline = () => {
      // Clear any existing debounce timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }

      // Only set online after 2 seconds of stable connection
      debounceTimerRef.current = setTimeout(() => {
        if (lastStateRef.current !== true) {
          setIsOnline(true)
          lastStateRef.current = true
        }
      }, 2000)
    }

    const handleOffline = () => {
      // Clear any existing debounce timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }

      // Wait 3 seconds before confirming offline status
      // This prevents false positives from brief interruptions
      debounceTimerRef.current = setTimeout(() => {
        if (lastStateRef.current !== false) {
          setIsOnline(false)
          lastStateRef.current = false
        }
      }, 3000)
    }

    // Listen for custom events from pwaUtils
    const handleAppOnline = () => handleOnline()
    const handleAppOffline = () => handleOffline()

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)
    document.addEventListener('app:online', handleAppOnline)
    document.addEventListener('app:offline', handleAppOffline)

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
      document.removeEventListener('app:online', handleAppOnline)
      document.removeEventListener('app:offline', handleAppOffline)
    }
  }, [])

  return isOnline
}

/**
 * React component that displays a message when the app is offline
 */
export function OfflineIndicator(): JSX.Element | null {
  const isOnline = useIsOnline()
  const [isVisible, setIsVisible] = useState(false)
  const [hasBeenOffline, setHasBeenOffline] = useState(false)
  const dismissTimerRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    if (!isOnline) {
      setIsVisible(true)
      setHasBeenOffline(true)
      // Clear any auto-dismiss timer when going offline
      if (dismissTimerRef.current) {
        clearTimeout(dismissTimerRef.current)
      }
    } else if (hasBeenOffline) {
      // Show the "Connection restored" message, even if the offline one was dismissed.
      setIsVisible(true)
      // Auto-dismiss after 5 seconds when connection is restored
      dismissTimerRef.current = setTimeout(() => {
        setIsVisible(false)
        setHasBeenOffline(false)
      }, 5000)
    }

    return () => {
      if (dismissTimerRef.current) {
        clearTimeout(dismissTimerRef.current)
      }
    }
  }, [isOnline, hasBeenOffline])

  if (!isVisible) {
    return null
  }

  return (
    <div
      style={{
        position: 'fixed',
        bottom: '1rem',
        left: '50%',
        transform: 'translateX(-50%)',
        background: isOnline ? '#4caf50' : '#ff9800', // Green for online, orange for offline
        color: 'white',
        padding: '0.75rem 1.5rem',
        borderRadius: '4px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
        transition: 'all 0.3s ease-in-out'
      }}>
      <span>{isOnline ? 'Connection restored' : 'Connection lost. Some features may be unavailable.'}</span>
      <button
        onClick={() => setIsVisible(false)}
        style={{
          background: 'transparent',
          border: 'none',
          color: 'white',
          cursor: 'pointer',
          padding: '0.25rem',
          fontSize: '1.2rem',
          lineHeight: 1,
          opacity: 0.8,
          transition: 'opacity 0.2s'
        }}
        onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
        onMouseLeave={(e) => (e.currentTarget.style.opacity = '0.8')}
        aria-label="Dismiss notification">
        ×
      </button>
    </div>
  )
}

/**
 * Higher-order component that adds offline functionality to a component
 * @param {React.ComponentType} Component The component to wrap
 * @returns {React.FC} A new component with offline awareness
 */
export function withOfflineDetection<P extends object>(Component: React.ComponentType<P>): React.FC<P & { isOnline?: boolean }> {
  return (props) => {
    const isOnline = useIsOnline()
    return (
      <Component
        {...props}
        isOnline={isOnline}
      />
    )
  }
}

/**
 * React hook to get the PWA installation status including cache information
 */
export function usePWAStatus() {
  const [status, setStatus] = useState({
    isInstalled: isInstalledPWA(),
    hasCachedData: false,
    hasQueuedRequests: false,
    isLoading: true
  })

  useEffect(() => {
    const checkCacheStatus = async () => {
      // Check for cached data
      let hasCachedData = false
      if ('caches' in window) {
        const cacheNames = await caches.keys()
        const apiCacheNames = cacheNames.filter((name) => name.includes('api-cache') || name.includes('default-cache'))

        for (const cacheName of apiCacheNames) {
          const cache = await caches.open(cacheName)
          const requests = await cache.keys()
          if (requests.length > 0) {
            hasCachedData = true
            break
          }
        }
      }

      // Check for queued requests
      const hasQueuedRequests = !!localStorage.getItem('offline_request_queue')

      setStatus((prev) => ({
        ...prev,
        hasCachedData,
        hasQueuedRequests,
        isLoading: false
      }))
    }

    checkCacheStatus()

    // Listen for display mode changes (browser to standalone or vice versa)
    const mediaQuery = window.matchMedia('(display-mode: standalone)')
    const listener = (event: MediaQueryListEvent) => {
      setStatus((prev) => ({
        ...prev,
        isInstalled: event.matches
      }))
    }

    mediaQuery.addEventListener('change', listener)
    return () => mediaQuery.removeEventListener('change', listener)
  }, [])

  return status
}

/**
 * Hook to provide functions for managing PWA cache
 */
export function usePWACache() {
  const [isClearing, setIsClearing] = useState(false)

  const clearCache = async (): Promise<boolean> => {
    setIsClearing(true)
    try {
      const result = await clearAllCaches()
      return result
    } finally {
      setIsClearing(false)
    }
  }

  return { clearCache, isClearing }
}
